import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import { ADO_AREA_PATHS_MAX, type AdoAreaPath } from "@ttc-spectree/toolbox-contract/fogbugz";
import { useAdoAreaPaths } from "@/hooks/queries/use-grooming";
import { ApiError } from "@/lib/api/client";
import { getAdoPatErrorCode, getApiErrorCode } from "@/lib/api/work-items";
import { adoSetupRedirect } from "@/lib/toolbox/page-names";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The ADO area-path picker of the FogBugz link editor (epic PI-SPEC-SW-E308,
 * feature PI-SPEC-SW-1610, task 1610-4; owner decision 2026-10-29: an area
 * path is PICKED, never typed, because a wrong path sends the FogBugz tools to
 * the wrong board).
 *
 * - A searchable combobox (the Repositories tab's `RepoCombobox` pattern) fed
 *   by `GET /grooming/projects/:project/ado-area-paths` (feature
 *   PI-SPEC-SW-1644: `map-project`, the caller's own ADO PAT). The first
 *   option is always "Project root" (value null); the paths follow in ADO's
 *   depth-first order, indented by depth, filtered case-insensitively on the
 *   whole path.
 * - Idle until the picker is first opened or its project changes, so a
 *   dialog with several pickers reads ADO only for the ones in use.
 * - No usable ADO PAT (ADO_PAT_REQUIRED / ADO_PAT_INVALID): "Connect ADO in
 *   Toolbox Setup to pick area paths." with a link to Setup, and only
 *   "Project root". No `map-project` there (403): "You need Project Manager
 *   on <project> to pick its area paths." A truncated list (more than
 *   2,000 areas) asks the person to type to filter.
 *
 * The server checks on save that every picked path still exists
 * (FOGBUGZ_LINK_AREA_PATH_NOT_FOUND).
 */

/** The label of the null choice: the ADO project's root area. */
export const PROJECT_ROOT_LABEL = "Project root";

/** The sentence for a missing or refused ADO PAT. */
export const CONNECT_ADO_TEXT = "Connect ADO in Toolbox Setup to pick area paths.";

/** The sentence for a caller without `map-project` on the picker's project. */
export function noRoleText(project: string): string {
  return `You need Project Manager on ${project} to pick its area paths.`;
}

/** The sentence for a list cut at the cap. */
export const TRUNCATED_TEXT = `Showing the first ${String(ADO_AREA_PATHS_MAX)} areas — type to filter.`;

/** A select-like control (design `.sel`), as the repository picker draws it. */
const SELECT_BOX =
  "flex h-8 w-full items-center gap-2 rounded-lg border border-[#3F3F46] bg-[#111113] px-2.5 text-[13px] text-foreground";

/** Why the list cannot be shown, in plain words; null when it can. Exported for tests. */
export function areaPathListProblem(
  error: unknown,
  project: string
): { text: string; connect: boolean } | null {
  if (!error) return null;
  if (getAdoPatErrorCode(error)) return { text: CONNECT_ADO_TEXT, connect: true };
  if (
    getApiErrorCode(error) === "TOOLBOX_CAPABILITY_REQUIRED" ||
    (error instanceof ApiError && error.response.status === 403)
  ) {
    return { text: noRoleText(project), connect: false };
  }
  return { text: "The area paths could not be read from Azure DevOps. Try again in a moment.", connect: false };
}

/**
 * Left padding per depth (depth 1 is flush with "Project root"), as literal
 * class names so Tailwind keeps them. ADO area trees are read to depth 10.
 */
const DEPTH_INDENT = [
  "pl-2",
  "pl-2",
  "pl-[22px]",
  "pl-9",
  "pl-[50px]",
  "pl-16",
  "pl-[78px]",
  "pl-[92px]",
  "pl-[106px]",
  "pl-[120px]",
  "pl-[134px]",
] as const;

function depthIndent(depth: number): string {
  return DEPTH_INDENT[Math.min(Math.max(depth, 0), DEPTH_INDENT.length - 1)] ?? "pl-2";
}

/** The pickable paths: everything below the project root (the root itself is the "Project root" choice). */
function belowRoot(paths: readonly AdoAreaPath[], project: string): AdoAreaPath[] {
  const root = project.toLowerCase();
  return paths.filter((entry) => entry.depth > 0 && entry.path.toLowerCase() !== root);
}

/** Set on the picker's root while its list is open. */
const OPEN_ATTR = "data-area-path-list-open";

/**
 * True when a key event comes from a picker whose list is open. An enclosing
 * Radix dialog uses this in `onEscapeKeyDown` so the first Escape closes only
 * the list (the picker handles it), not the whole dialog.
 */
export function isInOpenAreaPathList(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${OPEN_ATTR}]`) !== null;
}

export interface AdoAreaPathPickerProps {
  /** The ADO project whose area paths are listed. */
  project: string;
  /** The picked path, or null for the project root. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Accessible name of the combobox. */
  label?: string;
  disabled?: boolean;
  testId?: string;
}

export function AdoAreaPathPicker({
  project,
  value,
  onChange,
  label = "Area path",
  disabled = false,
  testId = "ado-area-path-picker",
}: AdoAreaPathPickerProps) {
  const location = useLocation();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);

  // A project chosen after the picker mounted (an override's ADO project) loads at once.
  const firstProject = useRef(project);
  useEffect(() => {
    if (project !== firstProject.current) setTouched(true);
  }, [project]);

  const paths = useAdoAreaPaths(project, { enabled: touched && !disabled });
  const problem = areaPathListProblem(paths.error, project);

  const shown = useMemo(() => {
    if (problem) return [];
    const all = belowRoot(paths.data?.paths ?? [], project);
    const wanted = query.trim().toLowerCase();
    return wanted ? all.filter((entry) => entry.path.toLowerCase().includes(wanted)) : all;
  }, [paths.data, project, problem, query]);

  /** Option 0 is "Project root"; option i + 1 is `shown[i]`. */
  const optionCount = shown.length + 1;

  const pick = (next: string | null) => {
    onChange(next);
    setQuery("");
    setOpen(false);
    setActive(-1);
  };

  const openList = () => {
    if (disabled) return;
    setTouched(true);
    setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openList();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => {
        const start = current < 0 ? (step === 1 ? -1 : 0) : current;
        return (start + step + optionCount) % optionCount;
      });
    } else if (event.key === "Enter") {
      if (open && active >= 0) {
        event.preventDefault();
        pick(active === 0 ? null : (shown[active - 1]?.path ?? null));
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  const optionId = (index: number) => `${listId}-${String(index)}`;
  const display = value ?? PROJECT_ROOT_LABEL;

  // Keep the keyboard-highlighted option visible as arrows move past the list's height.
  useEffect(() => {
    if (!open || active < 0) return;
    document.getElementById(`${listId}-${String(active)}`)?.scrollIntoView({ block: "nearest" });
  }, [open, active, listId]);

  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={testId} {...(open ? { [OPEN_ATTR]: "" } : {})}>
      <div className="relative min-w-0">
        <div className={cn(SELECT_BOX, open && "border-link", disabled && "opacity-50")}>
          <Search className={cn("h-4 w-4 shrink-0", TOOLBOX_TEXT.faint)} aria-hidden="true" />
          <input
            ref={inputRef}
            className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
            role="combobox"
            aria-label={label}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
            placeholder={display}
            value={open ? query : display}
            disabled={disabled}
            onFocus={openList}
            onClick={openList}
            onBlur={() => {
              setOpen(false);
              setActive(-1);
              setQuery("");
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              openList();
              setActive(-1);
            }}
            onKeyDown={onKeyDown}
            data-testid={`${testId}-input`}
          />
          <ChevronDown className={cn("h-4 w-4 shrink-0", TOOLBOX_TEXT.faint, open && "rotate-180")} aria-hidden="true" />
        </div>
        {open && (
          <div
            className="absolute left-0 top-[calc(100%+4px)] z-50 w-full min-w-[260px] rounded-[10px] border border-[#3F3F46] bg-[#18181B] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
            // Keep focus in the input while a click lands on an entry.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            <div className={cn(TOOLBOX_TEXT.eyebrow, "px-2 pb-1.5 pt-1")}>Area paths in {project}</div>
            <ul id={listId} role="listbox" aria-label={`Area paths in ${project}`} className="max-h-[240px] overflow-y-auto">
              <li
                id={optionId(0)}
                role="option"
                aria-selected={value === null}
                className={cn(
                  "flex cursor-pointer items-center rounded-md px-2 py-[7px] text-[13px] text-zinc-300 hover:bg-[#27272A] hover:text-foreground",
                  active === 0 && "bg-[#27272A] text-foreground"
                )}
                onMouseEnter={() => {
                  setActive(0);
                }}
                onClick={() => {
                  pick(null);
                }}
                data-testid="area-path-option"
              >
                {PROJECT_ROOT_LABEL}
              </li>
              {problem ? null : paths.isLoading ? (
                <li className={cn(TOOLBOX_TEXT.content, "px-2 py-1.5 text-muted-foreground")} role="presentation">
                  Reading the area paths from Azure DevOps…
                </li>
              ) : shown.length === 0 ? (
                <li className={cn(TOOLBOX_TEXT.content, "px-2 py-1.5 text-muted-foreground")} role="presentation">
                  {query.trim()
                    ? `No area path in ${project} matches "${query.trim()}".`
                    : `${project} has no areas below the project root.`}
                </li>
              ) : (
                shown.map((entry, index) => (
                  <li
                    key={entry.path}
                    id={optionId(index + 1)}
                    role="option"
                    aria-selected={value !== null && value.toLowerCase() === entry.path.toLowerCase()}
                    title={entry.path}
                    className={cn(
                      "flex cursor-pointer items-center rounded-md py-[7px] pr-2 text-[13px] text-zinc-300 hover:bg-[#27272A] hover:text-foreground",
                      depthIndent(entry.depth),
                      active === index + 1 && "bg-[#27272A] text-foreground"
                    )}
                    data-depth={entry.depth}
                    onMouseEnter={() => {
                      setActive(index + 1);
                    }}
                    onClick={() => {
                      pick(entry.path);
                    }}
                    data-testid="area-path-option"
                  >
                    <span className="min-w-0 truncate">{entry.name}</span>
                  </li>
                ))
              )}
            </ul>
            {!problem && paths.data?.truncated ? (
              <p className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint, "px-2 pt-1.5")} data-testid="area-paths-truncated">
                {TRUNCATED_TEXT}
              </p>
            ) : null}
          </div>
        )}
      </div>
      {problem ? (
        <p className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")} role="alert" data-testid="area-path-problem">
          {problem.text}
          {problem.connect ? (
            <>
              {" "}
              <Link
                className="text-link underline-offset-2 hover:underline"
                to={adoSetupRedirect(location.pathname, location.search)}
              >
                Open Toolbox Setup
              </Link>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
